import * as React from "react"
import Popover from "@mui/material/Popover"
import Button from "@mui/material/Button"
import { Stack } from "src/shared/components"
import { Divider, Theme } from "@mui/material"
import { Search } from "@mui/icons-material"
import TextRotateUpIcon from "@mui/icons-material/TextRotateUp"
import TextRotateVerticalIcon from "@mui/icons-material/TextRotateVertical"
import { Column } from "@tanstack/react-table"
import DebouncedInput from "src/shared/components/debounced-Input"
import InputAutocomplete from "src/shared/components/input-autocomplete"
interface FilterDataGridProps {
  children?: React.ReactNode
  anchorEl: any
  handleClose: Function
  anchorOrigin?: {
    vertical: "top" | "center" | "bottom"
    horizontal: "left" | "center" | "right"
  }
  buttonVariant?: "text" | "outlined" | "contained"
  buttonStyle?: React.CSSProperties
  column: Column<any, unknown>
  key?: string
  sorting?: any
  setSorting?: any
}
const FilterDataGrid: React.FC<FilterDataGridProps> = ({
  anchorEl,
  setSorting,
  handleClose,
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  column,
  key
}) => {
  const [filter, setFilter] = React.useState(column?.getFilterValue())
  const [sorted, setStorted] = React.useState(column.getIsSorted())
  const open = Boolean(anchorEl)
  const id = open ? `popover-${key}` : undefined
  const canFilter = column.getCanFilter()

  const handleClear = () => {
    column.clearSorting()
    column.setFilterValue(undefined)
    handleClose()
  }
  const onSubmit = () => {
    column?.setFilterValue(filter)
    if (sorted) setSorting(sorted === "desc" ? true : false)
    else column.clearSorting()
    handleClose()
  }

  const handleSort = (key: "asc" | "desc") => {
    const next = key === sorted ? false : key
    setStorted(next)
    // Sort-only mode (grid disabled column filters): apply immediately —
    // there is no Filter button to submit with.
    if (!canFilter) {
      if (next) setSorting(next === "desc" ? true : false)
      else column.clearSorting()
      handleClose()
    }
  }
  return (
    <Popover
      id={`Menu-${id}`}
      open={open}
      elevation={1}
      anchorEl={anchorEl}
      onClose={() => handleClose()}
      anchorOrigin={anchorOrigin}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Stack p={1} id={id}>
        {column.getCanSort() && (
          <>
            <Button
              sx={{
                justifyContent: "flex-start",
                ...(sorted === "asc" && { background: (theme) => theme.palette.action.hover })
              }}
              onClick={() => handleSort("asc")}
            >
              <TextRotateVerticalIcon sx={{ fontSize: (theme) => theme.typography.body1.fontSize, mr: 1 }} /> A to Z
            </Button>
            <Button
              sx={{
                justifyContent: "flex-start",
                ...(sorted === "desc" && { background: (theme) => theme.palette.action.hover })
              }}
              onClick={() => handleSort("desc")}
            >
              <TextRotateUpIcon sx={{ fontSize: (theme) => theme.typography.body1.fontSize, mr: 1 }} /> Z to A
            </Button>
            {canFilter && <Divider sx={{ mt: 1 }} />}
          </>
        )}
        <>
          <Stack direction={"row"} justifyContent={"flex-end"} sx={{ my: 1 }}>
            <Button
              sx={{ py: "2px", fontSize: (theme) => theme.typography.subtitle2.fontSize }}
              variant="outlined"
              onClick={handleClear}
            >
              Clear Filter
            </Button>
          </Stack>

          {canFilter && (
            <>
              {/* Filter Inputs Render here */}
              <FilterInput column={column} filter={filter} setFilter={setFilter} />

              <Stack direction={"row"} justifyContent={"flex-end"} sx={{ mt: 1 }}>
                <Button
                  sx={{ py: "2px", fontSize: (theme) => theme.typography.subtitle1.fontSize }}
                  variant="contained"
                  onClick={onSubmit}
                >
                  Filter
                </Button>
              </Stack>
            </>
          )}
        </>
      </Stack>
    </Popover>
  )
}

const FilterInput = ({
  column,
  filter,
  setFilter
}: {
  column: Column<any, unknown>
  filter: unknown
  setFilter: React.Dispatch<unknown>
}) => {
  const { filterVariant } = column.columnDef.meta ?? {}
  const options = column.columnDef.meta?.selectOptions ?? []
  if (!column.getCanFilter()) return <></>
  else if (filterVariant === "range")
    return (
      <Stack direction={"row"} spacing={2}>
        <DebouncedInput
          type="number"
          value={(filter as [number, number])?.[0] ?? ""}
          onChange={(value) => setFilter((old: [number, number]) => [value, old?.[1]])}
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(filter as [number, number])?.[1] ?? ""}
          onChange={(value) => setFilter((old: [number, number]) => [old?.[0], value])}
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </Stack>
    )
  else if (filterVariant === "select")
    return (
      <InputAutocomplete
        options={options}
        getOptionKey={(option) => option?.value}
        getOptionLabel={(option) => option?.label ?? option}
        sx={{
          "& input": { fontSize: (theme) => theme.typography.subtitle2.fontSize }
        }}
        slotProps={{
          paper: {
            sx: {
              "& .MuiAutocomplete-listbox ": {
                fontSize: (theme: Theme) => theme.typography.body2.fontSize
              }
            }
          }
        }}
        height="35px"
        value={options?.find((v) => (filter as string)?.includes(v.value)) as any}
        onChange={(value: any): any => setFilter(value.value)}
      />
    )
  // else if (filterVariant === "text")
  return (
    <DebouncedInput
      fullWidth
      id={`dataGrid-${column.id}`}
      onChange={(val) => setFilter(val as string)}
      value={filter as string}
      sx={{ fontSize: (theme) => theme.typography.body2.fontSize, height: "35px" }}
      placeholder={`Search...`}
      type="text"
      endAdornment={<Search sx={{ fontSize: (theme) => theme.typography.body1.fontSize }} />}
    />
  )
}

export default FilterDataGrid
